import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveMessage,
  discardMessage,
  listMessages,
  rotateInboxToken,
  updateAllowlist,
} from "@/lib/api/inbox";
import { workspaceKeys } from "@/hooks/use-workspaces";

export const inboxKeys = {
  all: ["inbox"] as const,
  messages: (workspaceId: string) => [...inboxKeys.all, "messages", workspaceId] as const,
};

export function useInboundMessages(workspaceId: string | undefined) {
  return useQuery({
    queryKey: inboxKeys.messages(workspaceId ?? ""),
    queryFn: () => listMessages(workspaceId!),
    enabled: Boolean(workspaceId),
    // Emails arrive while the page is open.
    refetchInterval: 15_000,
  });
}

function useInvalidate() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: inboxKeys.all });
    void queryClient.invalidateQueries({ queryKey: ["documents"] });
    void queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
  };
}

export function useApproveMessage() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, allowSender }: { id: string; allowSender: boolean }) =>
      approveMessage(id, allowSender),
    onSuccess: invalidate,
  });
}

export function useDiscardMessage() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: discardMessage, onSuccess: invalidate });
}

export function useUpdateAllowlist(workspaceId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (allowlist: string[]) => updateAllowlist(workspaceId, allowlist),
    onSuccess: invalidate,
  });
}

export function useRotateInboxToken(workspaceId: string) {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: () => rotateInboxToken(workspaceId), onSuccess: invalidate });
}
